import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { PersonFieldsType } from "../../../api/types";

interface TeamPersonListProps {
  persons: PersonFieldsType[];
  role: "staff" | "player";
  onAddPerson: () => void;
  onEditPerson: (id?: string) => void;
  onRemovePerson: (id?: string) => void;
}

export function EmptyList({
  role,
  onClick,
}: {
  role: "staff" | "player";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
    >
      <svg
        fill="none"
        stroke="currentColor"
        viewBox="0 0 48 48"
        aria-hidden="true"
        className="mx-auto w-12 text-gray-400"
      >
        <path
          d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="mt-2 block text-sm font-semibold text-gray-900">
        Ajouter un {role === "player" ? "joueur" : "membre du staff"}
      </span>
    </button>
  );
}

export default function TeamPersonList({
  role,
  persons,
  onAddPerson,
  onEditPerson,
  onRemovePerson,
}: TeamPersonListProps) {
  const isPlayer = role === "player";
  return (
    <>
      <EmptyList onClick={onAddPerson} role={role} />
      <ul className="divide-y divide-gray-100">
        {persons.map((person) => (
          <li key={person.id} className="flex justify-between gap-x-6 py-2">
            <div className="flex min-w-0 gap-x-4">
              <img
                alt=""
                src={person.image?.[0]?.url}
                className="w-12 h-12 flex-none rounded-full bg-gray-50"
              />
              <div className="min-w-0 flex-auto">
                <p className="text-sm/6 font-semibold text-gray-900">
                  <p className="hover:underline">{`${person.firstname} ${person.lastname}`}</p>
                </p>
                <p className="mt-1 flex text-xs/5 text-gray-500 truncate">
                  {person.licence_no ? (
                    <>
                      <span>{person.licence_no}</span>
                      <span className="mx-1.5">·</span>
                    </>
                  ) : null}
                  <strong className="capitalize">
                    {isPlayer ? `#${person.number}` : person.role}
                  </strong>
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-x-6">
              <div className="hidden sm:flex sm:flex-col sm:items-end">
                <p className="text-sm/6 text-gray-900">{person.licence_no}</p>
                {person.status === "inscrit" ? (
                  <p className="mt-1 text-xs/5 text-gray-500">
                    En attente de validation
                  </p>
                ) : (
                  <div className="mt-1 flex items-center gap-x-1.5">
                    <div className="flex-none rounded-full bg-emerald-500/20 p-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    </div>
                    <p className="text-xs/5 text-gray-500">Validé</p>
                  </div>
                )}
              </div>
              <Menu as="div" className="relative flex-none">
                <MenuButton className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                  <span className="sr-only">Open options</span>
                  <EllipsisVerticalIcon
                    aria-hidden="true"
                    className="w-5 h-5"
                  />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <MenuItem>
                    <button
                      onClick={() => onEditPerson(person?.id)}
                      className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                    >
                      Modifier les informations
                      <span className="sr-only">
                        , {`${person.firstname} ${person.lastname}`}
                      </span>
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={() => onRemovePerson(person?.id)}
                      className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                    >
                      Supprimer
                      <span className="sr-only">
                        , {`${person.firstname} ${person.lastname}`}
                      </span>
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
